import React, { useMemo, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/solid";
import {
  CheckCircleIcon as CheckCircleOutline,
  TruckIcon,
  ClockIcon as ClockOutline,
  ArrowPathIcon as ArrowPathOutline,
  ArrowUturnLeftIcon,
  XCircleIcon as XCircleOutline,
} from "@heroicons/react/24/outline";

import { useGetOrderByIdQuery } from "../../../services/order/orderApiSlice";
import { useUpdateOrderMutation } from "../../../services/order/orderMutationApiSlice";
import { useUpdateShipmentStatusMutation } from "../../../services/order/orderMutationApiSlice";
import { StatusPill } from "../orderTable/orderTableColumnsConfig";

import { CategoryPageSkeleton } from "../../../component/skeleton/CategoryPageSkeleton";
import { OrderDetailForm } from "../../../component/order/orderDetail/OrderDetailForm";
import classes from "./orderDetailPage.module.css";

const orderconfig = {
  pending: { label: "Pending", bg: "#FFF4E5", color: "#FF9500", Icon: ClockIcon },
  "in process": { label: "In Process", bg: "#E8F0FE", color: "#2563EB", Icon: ArrowPathIcon },
  "cancellation requested": {
    label: "Cancellation Requested",
    bg: "#FFF0F6", color: "#C21313", border: "1px solid #C21313",
    Icon: ExclamationTriangleIcon,
  },
  cancelled: { label: "Cancelled", bg: "#FDECEA", color: "#D92D20", Icon: XCircleIcon },
  "cancellation rejected": {
    label: "Cancellation Rejected", bg: "#F3E8FF", color: "#6D28D9", Icon: ShieldCheckIcon,
  },
  fulfilled: { label: "Fulfilled", bg: "#E6F7EF", color: "#00A167", Icon: CheckCircleIcon },
  failed: { label: "Failed", bg: "#FDECEA", color: "#B42318", Icon: XCircleIcon },
};

const paymentConfig = {
  pending:   { label: "Pending",   bg: "#FFF4E5", color: "#FF9500",  Icon: ClockIcon },
  paid:      { label: "Paid",      bg: "#E6F7EF", color: "#00A167",  Icon: CheckCircleIcon },
  cancelled: { label: "Cancelled", bg: "#FDECEA", color: "#D92D20",  Icon: XCircleIcon },
  failed:    { label: "Failed",    bg: "#FDECEA", color: "#D92D20",  Icon: XCircleIcon },
};

const shipmentStatusConfig = {
  delivered:          { label: "Delivered",         bg: "#E6F7EF", color: "#00A167", Icon: CheckCircleOutline },
  shipped:            { label: "Shipped",            bg: "#E0F2FE", color: "#0284C7", Icon: TruckIcon },
  "in process":       { label: "In Process",        bg: "#FEF3C7", color: "#D97706", Icon: ClockOutline },
  inprocess:          { label: "In Process",        bg: "#FEF3C7", color: "#D97706", Icon: ClockOutline },
  "in progress":      { label: "In Progress",       bg: "#FEF3C7", color: "#D97706", Icon: ClockOutline },
  "out for delivery": { label: "Out for Delivery",  bg: "#E8F0FE", color: "#2563EB", Icon: TruckIcon },
  "refund in process":{ label: "Refund in Process", bg: "#EEF2FF", color: "#6366F1", Icon: ArrowPathOutline },
  refund:             { label: "Refunded",           bg: "#F3F4F6", color: "#4B5563", Icon: ArrowUturnLeftIcon },
  rejected:           { label: "Rejected",           bg: "#FDECEC", color: "#E53935", Icon: XCircleOutline },
  cancel:             { label: "Cancelled",          bg: "#FDECEC", color: "#E53935", Icon: XCircleOutline },
  cancelled:          { label: "Cancelled",          bg: "#FDECEC", color: "#E53935", Icon: XCircleOutline },
  pending:            { label: "Pending",            bg: "#FFF4E5", color: "#FF8C00", Icon: ClockOutline },
};

export const ShipmentStatusBadge = ({ status, showTitle = true }) => {
  if (!status) return null;
  const key = status.toLowerCase().trim();
  const config = shipmentStatusConfig[key];
  if (!config) return null;
  const { label, bg, color, Icon } = config;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
      {showTitle && (
        <span style={{ fontSize: 13, fontWeight: 600, fontFamily: "Poppins, sans-serif", color: "#2f3567" }}>
          Status:
        </span>
      )}
      <div style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: "4px 14px", borderRadius: 999,
        backgroundColor: bg, color,
        fontSize: 12, fontWeight: 600,
        fontFamily: "Poppins, sans-serif", whiteSpace: "nowrap",
      }}>
        <Icon style={{ width: 14, height: 14 }} />
        {label}
      </div>
    </div>
  );
};

export const OrderDetailPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();

  const { data: orderDetail, isLoading, isError, isSuccess } = useGetOrderByIdQuery(orderId);
  const [updateOrder, { isLoading: isUpdating }] = useUpdateOrderMutation();
  const [updateShipmentStatus] = useUpdateShipmentStatusMutation();

  const shipments = useMemo(() => {
    const details = orderDetail?.order_info?.shipment_details || {};
    return Object.entries(details).map(([key, value], idx) => ({
      ...value,
      title: key
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase()),
    }));
  }, [orderDetail?.order_info?.shipment_details]);

  const handleUpdate = async (payload) => {
    try {
      const response = await updateOrder({ id: orderId, patchData: payload }).unwrap();
      toast.success(response?.message?.displayMessage || "Order updated successfully");
      navigate("/orders");
    } catch (err) {
      toast.error(
        err?.data?.message?.displayMessage || err?.error || "Failed to update order"
      );
    }
  };

  const handleShipmentChange = useCallback(
    async ({ shipment_id, status, statusLabel }) => {
      try {
        await updateShipmentStatus({ orderId, id: shipment_id, status }).unwrap();
        toast.success(`Shipment updated to ${statusLabel}`);
      } catch (err) {
        toast.error(
          err?.data?.message?.displayMessage || err?.error || "Failed to update shipment"
        );
      }
    },
    [orderId, updateShipmentStatus]
  );

  const orderStatusKey = orderDetail?.order_status?.toLowerCase() ?? "";
  const orderStatusConfig = orderconfig[orderStatusKey];

  const paymentStatusKey = orderDetail?.payment_status?.toLowerCase() ?? "";
  const paymentStatusConfig = paymentConfig[paymentStatusKey];

  return isSuccess ? (
    <div className={classes.wrapper}>
      <div className={classes.page}>
        <h2 className={classes.pageTitle}>{`Order ID: ${orderDetail.order_id}`}</h2>
        <div className={classes.page__payment}>
          <h2 className={classes.page__payment__title}>Order: </h2>
          {orderStatusConfig && <StatusPill {...orderStatusConfig} />}
        </div>
        <div className={classes.page__payment}>
          <h2 className={classes.page__payment__title}>Payment: </h2>
          {paymentStatusConfig && <StatusPill {...paymentStatusConfig} />}
        </div>
      </div>

      <OrderDetailForm
        initialValues={orderDetail}
        shipments={shipments}
        orderId={orderId}
        onShipmentChange={handleShipmentChange}
        onSubmit={handleUpdate}
        isSubmitting={isUpdating}
      />
    </div>
  ) : (
    <CategoryPageSkeleton />
  );
};
